// UI strings for "Missä paino?" (stressdrill/StressDrill.tsx; docs/PEDAGOGY.md §1.13, §8 phase 3):
// one word per screen, split into syllables — tap the stressed one, hear Peterson, see the rule.
// The rule line is built from the engine's weight analysis (engine stress-rule.ts) with the
// templates below, never written per word: `{s}` is the syllable (set in the HV face), `{r}` why
// it is heavy. Finnish (default) and English side by side; en has the same shape.
// Style: docs/FINNISH-STYLE.md (sinä-muoto, no jargon; numbers "7 kertaa 10:stä").
import { defineArea } from '../area';

/** "10:stä", "8:sta": the elative ending follows the Finnish numeral (kymmenestä, kahdeksasta). */
const ELATIVE: Record<number, string> = { 1: 'stä', 2: 'sta', 3: 'sta', 4: 'stä', 5: 'stä', 6: 'sta', 7: 'stä', 8: 'sta', 9: 'stä', 10: 'stä' };

export const stressDrillFi = {
  title: 'Missä paino?',
  /** The door (Ääntäminen, Tänään, Harjoittele viikkoa N). */
  door: 'Missä paino?',
  doorDetail: 'päättele, kuuntele · noin 5 min',
  position: (i: number, n: number) => `${i}/${n}`,
  /** The syllables' group (screen readers): what to do. */
  pick: 'Valitse tavu, jolla paino on',
  syllable: (i: number, n: number, s: string, weight: string) => `Tavu ${i}/${n}: ${s}${weight ? `, ${weight}` : ''}`,
  weight: { heavy: 'raskas', light: 'kevyt' },
  /** An exception's cue (before and after the answer): what the word is, as the check's "Sana on käsky." */
  cue: { command: 'käsky', lengthening: 'ja-pidennys' },
  /** While Peterson's clip plays (after the tap). */
  hearing: 'Kuuntele Petersonia.',
  showAnswer: 'Näytä vastaus',
  failed: 'Äänite ei soinut. Vastaus näkyy silti.',
  right: 'Oikein.',
  wrong: 'Ei aivan.',
  /** "Paino on tavulla {s}." */
  stressedOn: 'Paino on tavulla {s}.',
  /** Why, by the rule (engine rules/stress-weight.ts). */
  why: {
    'two-syllables': 'Kaksi tavua → paino toiseksi viimeiselle eli ensimmäiselle tavulle.',
    'penult-heavy': 'Toiseksi viimeinen tavu {s} on raskas ({r}) → paino siihen.',
    'antepenult-heavy': 'Toiseksi viimeinen tavu on kevyt ja sitä edeltävä {s} raskas ({r}) → paino kolmanneksi viimeiseen.',
    'both-light': 'Toiseksi ja kolmanneksi viimeinen tavu ovat kevyitä → paino jää toiseksi viimeiseen, {s}.',
    command: 'Käskymuoto → paino viimeiselle tavulle.',
    lengthening: 'Ja-pidennys: luettelon viimeinen sana → paino viimeiselle tavulle.',
  },
  reason: { closed: 'päättyy konsonanttiin', long: 'pitkä vokaali', diphthong: 'diftongi' },
  exception: 'Poikkeus',
  replay: 'Kuuntele uudelleen',
  next: 'Seuraava',
  done: 'Valmis',
  /** The session's end. */
  end: {
    title: 'Kierros tehty',
    score: (hits: number, n: number) => (ELATIVE[n] ? `Osuit ${hits} kertaa ${n}:${ELATIVE[n]}.` : `Osuit ${hits}/${n}.`),
    missed: 'Kuuntele nämä vielä',
    again: 'Uusi kierros',
  },
  ledger: 'Tämä kierros',
  hit: 'osui',
  miss: 'ei osunut',
  rules: 'Painon säännöt',
  noWeek: 'Missä paino? avautuu ensimmäisen oppitunnin kanssa.',
  empty: 'Avoimilla viikoilla ei vielä ole Petersonin äänitteitä, joista painon kuulee.',
  emptyWeek: (week: number) => `Viikolla ${week} ei ole Petersonin äänitteitä, joista painon kuulee.`,
  allWeeks: 'Kaikki avoimet viikot',
  weekScope: (week: number) => `Viikko ${week}`,
  loading: 'Ladataan sanakirjaa…',
};

export const stressDrillEn: typeof stressDrillFi = {
  title: 'Where is the stress?',
  door: 'Where is the stress?',
  doorDetail: 'work it out, then listen · about 5 min',
  position: (i, n) => `${i}/${n}`,
  pick: 'Choose the stressed syllable',
  syllable: (i, n, s, weight) => `Syllable ${i}/${n}: ${s}${weight ? `, ${weight}` : ''}`,
  weight: { heavy: 'heavy', light: 'light' },
  cue: { command: 'command', lengthening: '“and” lengthening' },
  hearing: 'Listen to Peterson.',
  showAnswer: 'Show the answer',
  failed: 'The recording did not play. Here is the answer anyway.',
  right: 'Right.',
  wrong: 'Not quite.',
  stressedOn: 'The stress is on {s}.',
  why: {
    'two-syllables': 'Two syllables → the stress goes on the penult, the first syllable.',
    'penult-heavy': 'The penult {s} is heavy ({r}) → the stress goes there.',
    'antepenult-heavy': 'The penult is light and the one before it, {s}, heavy ({r}) → the stress goes on the antepenult.',
    'both-light': 'The penult and the antepenult are both light → the stress stays on the penult, {s}.',
    command: 'A command → the stress goes on the last syllable.',
    lengthening: '“And” lengthening: the last word of a list → the stress goes on the last syllable.',
  },
  reason: { closed: 'ends in a consonant', long: 'long vowel', diphthong: 'diphthong' },
  exception: 'Exception',
  replay: 'Listen again',
  next: 'Next',
  done: 'Done',
  end: {
    title: 'Round done',
    score: (hits, n) => `You got ${hits} out of ${n}.`,
    missed: 'Listen to these again',
    again: 'Another round',
  },
  ledger: 'This round',
  hit: 'right',
  miss: 'missed',
  rules: 'The stress rules',
  noWeek: 'Where is the stress? opens with the first lesson.',
  empty: 'The open weeks have no Peterson recordings yet to hear the stress in.',
  emptyWeek: (week) => `Week ${week} has no Peterson recordings to hear the stress in.`,
  allWeeks: 'All open weeks',
  weekScope: (week) => `Week ${week}`,
  loading: 'Loading the dictionary…',
};

// Messages.stressDrill (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('stressDrill', stressDrillFi, stressDrillEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof stressDrillFi> {}
}
